import { Provider } from '@angular/core';
import { injectDispatch } from '@ngrx/signals/events';
import { StoreCoordinationEvents } from '@app/domain/events';
import { CategoryDomainService } from '@app/domain/services';
import {
  GetCategoriesUseCase,
  GetCategoryByIdUseCase,
  GetCategoriesByTypeUseCase,
  GetCategoriesByParentUseCase,
  CreateCategoryUseCase,
  UpdateCategoryUseCase,
  DeleteCategoryUseCase,
  RemapCategoryOffsetsUseCase,
  ReorderCategoriesUseCase,
} from '@app/domain/use-cases';
import type { ICategoryRepository, ITransactionManager } from '@app/domain/ports';
import { CATEGORY_REPOSITORY, TRANSACTION_MANAGER } from '../../tokens/infrastructure.tokens';

/**
 * Provides Category domain services and use cases.
 *
 * Includes:
 * - CategoryDomainService: Business rules and validation
 * - 8 Use Cases: GetCategories, GetCategoryById, GetByType, GetByParent, Create, Update, Delete, Reorder
 *
 * All providers use constructor injection pattern following Clean Architecture.
 */
export function provideCategoryDomain(): Provider[] {
  return [
    // Domain Service
    {
      provide: CategoryDomainService,
      useFactory: () => new CategoryDomainService(),
      deps: [],
    },

    // Use Cases - Query
    {
      provide: GetCategoriesUseCase,
      useFactory: (repository: ICategoryRepository) => new GetCategoriesUseCase(repository),
      deps: [CATEGORY_REPOSITORY],
    },
    {
      provide: GetCategoryByIdUseCase,
      useFactory: (repository: ICategoryRepository) => new GetCategoryByIdUseCase(repository),
      deps: [CATEGORY_REPOSITORY],
    },
    {
      provide: GetCategoriesByTypeUseCase,
      useFactory: (repository: ICategoryRepository) => new GetCategoriesByTypeUseCase(repository),
      deps: [CATEGORY_REPOSITORY],
    },
    {
      provide: GetCategoriesByParentUseCase,
      useFactory: (repository: ICategoryRepository) => new GetCategoriesByParentUseCase(repository),
      deps: [CATEGORY_REPOSITORY],
    },

    // Use Cases - Commands
    {
      provide: CreateCategoryUseCase,
      useFactory: (repository: ICategoryRepository, domainService: CategoryDomainService) => {
        const dispatch = injectDispatch(StoreCoordinationEvents);
        return new CreateCategoryUseCase(repository, domainService, dispatch);
      },
      deps: [CATEGORY_REPOSITORY, CategoryDomainService],
    },
    {
      provide: UpdateCategoryUseCase,
      useFactory: (repository: ICategoryRepository, domainService: CategoryDomainService) => {
        const dispatch = injectDispatch(StoreCoordinationEvents);
        return new UpdateCategoryUseCase(repository, domainService, dispatch);
      },
      deps: [CATEGORY_REPOSITORY, CategoryDomainService],
    },
    {
      provide: DeleteCategoryUseCase,
      useFactory: (
        repository: ICategoryRepository,
        domainService: CategoryDomainService,
        transactionManager: ITransactionManager,
      ) => {
        const dispatch = injectDispatch(StoreCoordinationEvents);
        return new DeleteCategoryUseCase(repository, domainService, dispatch, transactionManager);
      },
      deps: [CATEGORY_REPOSITORY, CategoryDomainService, TRANSACTION_MANAGER],
    },
    {
      provide: RemapCategoryOffsetsUseCase,
      useFactory: (repository: ICategoryRepository) => new RemapCategoryOffsetsUseCase(repository),
      deps: [CATEGORY_REPOSITORY],
    },
    {
      provide: ReorderCategoriesUseCase,
      useFactory: (repository: ICategoryRepository) => {
        const dispatch = injectDispatch(StoreCoordinationEvents);
        return new ReorderCategoriesUseCase(repository, dispatch);
      },
      deps: [CATEGORY_REPOSITORY],
    },
  ];
}
